import { forwardRef, type AriaAttributes, type KeyboardEventHandler } from "react";
import styled, { keyframes, useTheme } from "styled-components";
import { ITheme, RecursiveCSS } from "../theme/GlobalTheme";

/*| SystemCssProperties<Theme>
  | CSSPseudoSelectorProps<Theme>
  | CSSSelectorObjectOrCssVariables<Theme>
  | null;*/

type CSSObject = Record<string, any>;

const UNITLESS_PROPS = new Set(["opacity", "zIndex", "fontWeight", "lineHeight", "flex", "flexGrow", "flexShrink", "order", "zoom"]);

const PX_PROPS = new Set([
	"width",
	"height",
	"minWidth",
	"minHeight",
	"maxWidth",
	"maxHeight",
	"top",
	"right",
	"bottom",
	"left",
	"margin",
	"marginTop",
	"marginRight",
	"marginBottom",
	"marginLeft",
	"padding",
	"paddingTop",
	"paddingRight",
	"paddingBottom",
	"paddingLeft",
	"gap",
	"rowGap",
	"columnGap",
	"borderRadius",
	"fontSize",
]);

function toKebabCase(prop: string): string {
	if (prop.startsWith("--")) return prop;
	return prop.replace(/([A-Z])/g, "-$1").toLowerCase();
}

function normalizeValue(prop: string, value: any): string | null {
	if (value === undefined || value === null || value === false) return null;

	if (value === "") return null;

	// Numbers: add px for common dimensional props unless unitless
	if (typeof value === "number") {
		if (UNITLESS_PROPS.has(prop)) return String(value);
		if (PX_PROPS.has(prop)) return `${value}px`;
		return String(value);
	}

	if (typeof value === "string") {
		return value.trim();
	}

	if (typeof value === "boolean") return null;

	return String(value);
}

/**
 * Deterministic CSS generation:
 * - Stable key order (sorted)
 * - Better handling of nested selectors (@media, &:hover, etc.)
 * - Basic value normalization
 */
export function createCSSFromObject(css: React.CSSProperties | CSSObject): string {
	if (!css || typeof css !== "object") return "";

	let rules = "";

	const keys = Object.keys(css).sort(); // critical: deterministic order

	for (const key of keys) {
		const raw = (css as any)[key];
		if (raw === undefined || raw === null || raw === false || raw === "") continue;

		// Nested blocks: selectors / media queries
		if (typeof raw === "object" && !Array.isArray(raw)) {
			// key can be "&:hover", ":hover", "@media (...)", "svg path", etc.
			// DO NOT kebab-case selector keys.
			const nested = createCSSFromObject(raw);

			if (!nested) continue;

			// If it's a selector-ish key, emit as block
			// Examples:
			// "&:hover" { ... }
			// "@media (min-width: 768px)" { ... }
			// "svg path" { ... }
			rules += `${key}{\n${nested}}\n`;
			continue;
		}

		const propName = toKebabCase(key);
		const value = normalizeValue(key, raw);
		if (value === null) continue;

		rules += `${propName}: ${value};\n`;
	}

	return rules;
}

const splitBoxStyles = (css?: RecursiveCSS) => {
	if (!css) return { inlineStyle: undefined, dynamicSx: undefined };

	const nestedProperties = new Set<string>();
	const collectNestedProperties = (block: CSSObject) => {
		for (const [key, value] of Object.entries(block)) {
			if (value && typeof value === "object" && !Array.isArray(value)) {
				collectNestedProperties(value as CSSObject);
			} else {
				nestedProperties.add(key);
			}
		}
	};

	for (const value of Object.values(css)) {
		if (value && typeof value === "object" && !Array.isArray(value)) {
			collectNestedProperties(value as CSSObject);
		}
	}

	const inlineStyle: React.CSSProperties = {};
	const dynamicSx: RecursiveCSS = {};

	for (const [key, value] of Object.entries(css)) {
		if ((value && typeof value === "object" && !Array.isArray(value)) || nestedProperties.has(key)) {
			(dynamicSx as CSSObject)[key] = value;
		} else {
			(inlineStyle as CSSObject)[key] = value;
		}
	}

	return {
		inlineStyle: Object.keys(inlineStyle).length ? inlineStyle : undefined,
		dynamicSx: Object.keys(dynamicSx).length ? dynamicSx : undefined,
	};
};

/*OLD
export function createCSSFromObject(css: React.CSSProperties): string {
	let rules = "";
	for (let i in css) {
		if (!(css as any)[i]) continue;
		const name = i.replaceAll(/([A-Z])/g, "-$1").toLowerCase();
		const param = (css as any)[i];
		if (typeof param === "object") {
			rules += `${name}{\n\t` + createCSSFromObject(param) + `};\n`;
		} else rules += `${name}: ${param};\n`;
	}
	return rules;
}
*/
export interface IBoxProps {
	children?: any;
	sx?: RecursiveCSS;
	onClick?: (e?: MouseEvent) => void;
	onMouseOver?: (index: number) => void;
	index?: number;
	element?: "div" | "input" | "a" | "button" | "span";
	variant?: string;
	type?: string;
	value?: string;
	disabled?: boolean;
	role?: string;
	tabIndex?: number;
	onKeyDown?: KeyboardEventHandler<HTMLElement>;
	"aria-label"?: string;
	"aria-modal"?: boolean;
	"aria-expanded"?: AriaAttributes["aria-expanded"];
	"aria-controls"?: AriaAttributes["aria-controls"];
	"aria-checked"?: AriaAttributes["aria-checked"];
	"aria-haspopup"?: AriaAttributes["aria-haspopup"];
	"aria-disabled"?: AriaAttributes["aria-disabled"];
	"aria-selected"?: AriaAttributes["aria-selected"];
	title?: string;
	style?: React.CSSProperties;
	vflex?: boolean;
	cflex?: boolean;
	scrollable?: boolean;
	id?: string;
	animate?: boolean;
}

interface IBoxDivProps {
	$sx?: React.CSSProperties;
	$animate?: boolean;
}

const boxAnimateFadeIn = keyframes`
    0% {
      opacity: .0;
      transform: translate(0,50vh);
    }
    100% {
      opacity: 1;
      transform: translate(0,0);
    }
`;

const BoxDiv = styled.div<IBoxDivProps>`
	box-sizing: border-box;
	${({ $sx }) => {
		return $sx ? createCSSFromObject($sx) : "";
	}}
	&[data-animate="true"] {
		animation: ${boxAnimateFadeIn} 0.3s cubic-bezier(0.5, 0, 0.5, 1);
	}
`;

const BoxInput = styled.input<IBoxDivProps>`
	box-sizing: border-box;
	${({ $sx }) => {
		return $sx ? createCSSFromObject($sx) : "";
	}}
`;

const setupColors = (tsx?: React.CSSProperties, theme?: ITheme) => {
	if (!theme || !theme.colors || !tsx) return;

	for (let i in tsx) {
		const value = (tsx as any)[i];

		if (typeof value === "object") {
			setupColors(value, theme);
			continue;
		}

		if (!value || typeof value !== "string") {
			continue;
		}

		// simple color change
		if ((theme.colors as any)[value]) {
			(tsx as any)[i] = (theme.colors as any)[value];
		} else {
			// change color in string
			for (let j in theme.colors) {
				if (value.indexOf(j) > -1) (tsx as any)[i] = value.replace(j, (theme.colors as any)[j]);
			}
		}
	}
};

export const Box = forwardRef((props: IBoxProps, ref) => {
	const theme = useTheme() as ITheme;
	const { children, sx, title, style } = props;
	const data: any = {};
	for (let i in props) {
		if (i.indexOf("data-") === 0) data[i] = (props as any)[i];
	}

	let tsx = sx;
	if (props.vflex) {
		if (!tsx) tsx = {};
		tsx.display = "flex";
		tsx.flexDirection = "column";
	}

	if (props.cflex) {
		if (!tsx) tsx = {};
		tsx.display = "flex";
		tsx.flexDirection = "column";
		tsx.alignItems = "center";
		tsx.justifyContent = "center";
	}

	setupColors(tsx, theme);

	if (props.element && props.element === "input") {
		const { inlineStyle, dynamicSx } = splitBoxStyles(tsx);
		return (
			<BoxInput
				ref={ref ?? null}
				id={props.id}
				{...data}
				style={{ ...inlineStyle, ...style }}
				onClick={props.onClick}
				$sx={dynamicSx}
				type={props.type}
				onChange={() => {}}
				value={props.value}
			/>
		);
	}

	let content = children;
	if (props.scrollable) {
		if (!tsx) tsx = {};
		tsx.position = "relative";
		tsx.overflow = "auto";
		content = <Box sx={{}}>{children}</Box>;
	}
	const { inlineStyle, dynamicSx } = splitBoxStyles(tsx);

	return (
		<BoxDiv
			as={props.element ?? "div"}
			data-animate={props.animate}
			ref={ref ?? null}
			id={props.id}
			title={title}
			type={props.element === "button" ? props.type ?? "button" : undefined}
			disabled={props.element === "button" ? props.disabled : undefined}
			role={props.role}
			tabIndex={props.tabIndex}
			onKeyDown={props.onKeyDown}
			aria-label={props["aria-label"]}
			aria-modal={props["aria-modal"]}
			aria-expanded={props["aria-expanded"]}
			aria-controls={props["aria-controls"]}
			aria-checked={props["aria-checked"]}
			aria-haspopup={props["aria-haspopup"]}
			aria-disabled={props["aria-disabled"]}
			aria-selected={props["aria-selected"]}
			style={{ ...inlineStyle, ...style }}
			{...data}
			onClick={props.onClick}
			onMouseOver={_e => {
				props.onMouseOver && props.index !== undefined && props.onMouseOver(props.index);
			}}
			$sx={dynamicSx}>
			{content}
		</BoxDiv>
	);
});

export const HBox = (props: IBoxProps & { gap?: string; center?: boolean }) => {
	const p = { ...props };

	if (p.gap === "tiny") p.gap = "4px";

	p.sx = { ...p.sx, display: "flex", position: "relative", transition: "all 0.2s ease" };

	if (p.gap) p.sx = { ...p.sx, gap: p.gap };
	if (p.center) p.sx = { ...p.sx, justifyContent: "center", alignItems: "center" };

	if (p.variant === "left-center") p.sx = { ...p.sx, justifyContent: "flex-start", alignItems: "center", gap: p.gap ?? "10px" };

	return <Box {...p} />;
};

export const VBox = forwardRef((props: IBoxProps & { gap?: string; center?: boolean; spacer?: "normal" | "tiny" }, ref) => {
	let { gap } = props;
	if (gap === "tiny") gap = "4px";
	const p = { ...props };
	p.sx = { ...p.sx, display: "flex", flexDirection: "column" };
	if (gap) p.sx = { ...p.sx, gap: gap };
	if (p.center) p.sx = { ...p.sx, justifyContent: "center", alignItems: "center" };
	if (p.spacer === "tiny") p.sx = { ...p.sx, gap: "4px" };
	if (p.spacer === "normal") p.sx = { ...p.sx, gap: "10px" };
	return <Box ref={ref} {...p} />;
});

export const PanelBox = forwardRef((props: IBoxProps & { size?: "small" }, ref) => {
	const p = { ...props };
	p.sx = {
		display: "flex",
		flexDirection: "column",
		padding: "20px",
		borderRadius: "6px",
		boxShadow: "0px 1px 20px rgba(0,0,0,.1)",
		...p.sx,
	};

	if (props.variant === "strict") {
		p.sx = {
			...p.sx,
			boxShadow: "0 1px 0px rgba(0,0,0,0.2)",
			backgroundColor: "#FFF",
		};
	}

	if (props.variant === "light") {
		p.sx = {
			...p.sx,
			boxShadow: "none",
			padding: "20px 20px",
			border: "1px dotted #a6b6d1",
			backgroundColor: "#eff5ff",
		};
	}

	if (props.variant === "outlined") {
		p.sx = {
			...p.sx,
			boxShadow: "none",
			padding: "20px 20px",
			border: "1px dotted #a6b6d1",
			//backgroundColor: "#eff5ff",
		};
	}

	if (props.variant === "no-records") {
		p.sx = {
			...p.sx,
			boxShadow: "none",
			padding: "40px 20px",
			border: "1px dashed #a6b6d1",
			backgroundColor: "#f9fbff",
			alignItems: "center",
		};
	}

	const s: RecursiveCSS = {};
	if (props.onClick) {
		p.sx.cursor = "pointer";
		p.sx.transition = "background-color 0.2s ease";
		p.sx["&:hover"] = {
			...p.sx["&:hover"],
			backgroundColor: "#a6b6d1",
		};
	}

	return <Box ref={ref} {...p} style={s} />;
});

export const ScrollBox = forwardRef((props: IBoxProps, ref) => {
	const p = { ...props };
	p.children = <div>{props.children}</div>;
	p.sx = {
		...p.sx,
		flexGrow: "1",
		position: "relative",
		overflow: "auto",
		height: "100%",
		"&>div": {
			position: "absolute",
			width: "100%",
		},
	};
	return <Box ref={ref} {...p} />;
});

export const isCSS = (possibleCSS: any): boolean => {
	if (typeof possibleCSS !== "object") return false;
	const cssKeys = [
		"animation",
		"transition",
		"border",
		"borderTop",
		"borderRight",
		"borderBottom",
		"borderLeft",
		"opacity",
		"backgroundColor",
		"color",
		"border",
		"borderRadius",
		"boxShadow",
		"cursor",
		"display",
		"gridColumn",
		"gridRow",
		"gridTemplateColumns",
		"gridTemplateRows",
		"height",
		"width",
		"margin",
		"padding",
		"position",
		"top",
		"left",
		"right",
		"bottom",
		"overflow",
		"overflowX",
		"overflowY",
		"zIndex",
		"justifyContent",
		"alignItems",
		"alignContent",
		"flexDirection",
		"flexWrap",
		"flexGrow",
		"flexShrink",
		"flexBasis",
		"flex",
		"gap",
		"rowGap",
		"columnGap",
		"placeContent",
		"placeItems",
		"placeSelf",
		"textAlign",
		"textTransform",
		"textDecoration",
		"textOverflow",
		"whiteSpace",
		"overflowWrap",
		"wordBreak",
		"lineHeight",
		"fontFamily",
		"fontSize",
		"fontWeight",
		"fontStyle",
		"letterSpacing",
		"textShadow",
		"textIndent",
		"textJustify",
		"textOrientation",
		"textRendering",
		"textSizeAdjust",
		"textUnderlinePosition",
		"textTransform",
		"textWrap",
		"textEmphasis",
		"textEmphasisColor",
		"textEmphasisStyle",
		"textEmphasisPosition",
		"textEmphasisFill",
		"textEmphasisCustom",
		"textStroke",
		"textStrokeColor",
		"textStrokeWidth",
	];

	for (let i in possibleCSS) {
		if (cssKeys.indexOf(i) > -1) return true;
	}

	return false;
};
